import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Code, TrendingUp, Share2, Target, Palette, Server, Video } from 'lucide-react';
import { services } from '../../mock';

const iconMap = { Code, TrendingUp, Share2, Target, Palette, Server, Video };

// Skill tags for each service to fill cards properly
const serviceTags = {
  1: ['React', 'Node.js', 'MongoDB', 'WordPress'],
  2: ['Google Analytics', 'Search Console', 'Keyword Research', 'Technical SEO'],
  3: ['Instagram', 'Facebook', 'LinkedIn', 'Content Calendar'],
  4: ['Facebook Ads', 'Instagram Ads', 'Conversion Tracking', 'A/B Testing'],
  5: ['Figma', 'Brand Identity', 'Logo Design', 'Visual Strategy'],
  6: ['Domain Setup', 'Hosting', 'Email Config', 'SSL & Security'],
  7: ['Reels', 'Short-Form Video', 'CapCut', 'DaVinci Resolve']
};

const BoldServicesSection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [hoveredCard, setHoveredCard] = useState(null);

  return (
    <section id="services" className="py-32 px-6 relative">
      <div className="section-watermark" style={{ top: '5%', left: '-5%' }}>03</div>
      
      <div className="max-w-7xl mx-auto" ref={ref}>
        <motion.h2
          className="text-center mb-24 gradient-text"
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          style={{ fontFamily: 'Clash Display, Inter, sans-serif' }}
        >
          What I do
        </motion.h2>
        
        {/* Clean Even Grid - 3 columns desktop, 2 tablet, 1 mobile */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => {
            const Icon = iconMap[service.icon];
            const tags = serviceTags[service.id] || [];
            
            return (
              <motion.div
                key={service.id}
                className="relative overflow-hidden h-full"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                onMouseEnter={() => setHoveredCard(service.id)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <motion.div
                  className="h-full p-8 bg-[#1A1430] border-2 border-[#7C3AED]/20 rounded-3xl transition-all duration-300 relative group flex flex-col"
                  style={{
                    transformStyle: 'preserve-3d',
                  }}
                  whileHover={{
                    scale: 1.03,
                    rotateX: 2,
                    rotateY: 2,
                    boxShadow: '0 20px 60px rgba(124, 58, 237, 0.3), 0 0 0 1px rgba(255, 92, 57, 0.5), inset 0 0 80px rgba(124, 58, 237, 0.1)',
                    borderColor: 'rgba(255, 92, 57, 0.8)'
                  }}
                >
                  {/* Gradient Glow on Hover */}
                  <motion.div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-3xl"
                    style={{
                      background: 'radial-gradient(circle at 50% 50%, rgba(124, 58, 237, 0.15), transparent 70%)'
                    }}
                  />
                  
                  {/* Content - Flex Column, No Empty Space */}
                  <div className="relative z-10 flex flex-col">
                    <motion.div
                      className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#7C3AED] to-[#C026D3] flex items-center justify-center mb-6 group-hover:shadow-2xl group-hover:shadow-[#7C3AED]/50 transition-shadow"
                      whileHover={{ rotate: 360 }}
                      transition={{ duration: 0.6 }}
                    >
                      {Icon && <Icon size={32} className="text-white" />}
                    </motion.div>
                    
                    <h3 className="text-2xl font-bold text-[#EDE9FE] mb-4 group-hover:text-[#FF5C39] transition-colors" style={{ fontFamily: 'Clash Display, Inter, sans-serif' }}>
                      {service.title}
                    </h3>
                    
                    <p className="text-[#9D97B5] leading-relaxed text-lg group-hover:text-[#EDE9FE] transition-colors mb-4">
                      {service.description}
                    </p>
                    
                    {/* Skill Tags - Directly Under Description, No Gap */}
                    <div className="flex flex-wrap gap-2 mb-2">
                      {tags.map((tag, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 text-xs font-medium rounded-full bg-[#7C3AED]/10 text-[#7C3AED] border border-[#7C3AED]/20 group-hover:bg-[#FF5C39]/10 group-hover:text-[#FF5C39] group-hover:border-[#FF5C39]/20 transition-colors"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    
                    {/* Video Editing: Link to Social Media projects */}
                    {service.id === 7 ? (
                      <p className="text-xs text-[#9D97B5]/60">
                        See edits live on the{' '}
                        <a 
                          href="#social-media" 
                          className="text-[#FF5C39] hover:text-[#C026D3] transition-colors underline"
                        >
                          social pages I manage →
                        </a>
                      </p>
                    ) : (
                      <p className="text-xs text-[#9D97B5]/60 italic">
                        Tools and platforms I use daily
                      </p>
                    )}
                  </div>
                </motion.div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default BoldServicesSection;